import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  MALOCCLUSION_CLASSES,
  ORTHODONTIC_APPLIANCES,
  ORTHODONTIC_STATUSES,
} from '../../constants/odontology';
import type { OrthodonticActivation, OrthodonticTreatment } from '../../types/odontology';
import { createOrthodonticTreatment, updateOrthodonticTreatment } from '../../services/odontologyService';
import { todayIso, useDentalProfessionals } from './odontologyHooks';

export interface OrthodonticTreatmentDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  unitId?: string | null;
  treatment?: OrthodonticTreatment | null;
  defaultProfessionalId?: string | null;
  onSaved: () => void;
}

const OrthodonticTreatmentDialog: React.FC<OrthodonticTreatmentDialogProps> = ({
  open,
  onClose,
  patientId,
  unitId,
  treatment,
  defaultProfessionalId,
  onSaved,
}) => {
  const [applianceType, setApplianceType] = useState('');
  const [malocclusionClass, setMalocclusionClass] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [objectives, setObjectives] = useState('');
  const [startedDate, setStartedDate] = useState('');
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [installationFee, setInstallationFee] = useState('');
  const [monthlyFee, setMonthlyFee] = useState('');
  const [activations, setActivations] = useState<OrthodonticActivation[]>([]);
  const [retentionPlan, setRetentionPlan] = useState('');
  const [status, setStatus] = useState('planned');
  const [professionalId, setProfessionalId] = useState('');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const professionals = useDentalProfessionals(open);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (treatment) {
      setApplianceType(treatment.appliance_type || '');
      setMalocclusionClass(treatment.malocclusion_class || '');
      setDiagnosis(treatment.diagnosis || '');
      setObjectives(treatment.objectives || '');
      setStartedDate(treatment.started_date?.slice(0, 10) || '');
      setExpectedEndDate(treatment.expected_end_date?.slice(0, 10) || '');
      setCompletedDate(treatment.completed_date?.slice(0, 10) || '');
      setInstallationFee(treatment.installation_fee != null ? String(treatment.installation_fee) : '');
      setMonthlyFee(treatment.monthly_fee != null ? String(treatment.monthly_fee) : '');
      setActivations(treatment.activations || []);
      setRetentionPlan(treatment.retention_plan || '');
      setStatus(treatment.status || 'planned');
      setProfessionalId(treatment.professional_id || '');
      setNotes(treatment.notes || '');
    } else {
      setApplianceType('');
      setMalocclusionClass('');
      setDiagnosis('');
      setObjectives('');
      setStartedDate('');
      setExpectedEndDate('');
      setCompletedDate('');
      setInstallationFee('');
      setMonthlyFee('');
      setActivations([]);
      setRetentionPlan('');
      setStatus('planned');
      setProfessionalId(defaultProfessionalId || '');
      setNotes('');
    }
  }, [open, treatment, defaultProfessionalId]);

  const updateActivation = (index: number, patch: Partial<OrthodonticActivation>) => {
    setActivations((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const numberOrZero = (value: string) => {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        patient_id: patientId,
        unit_id: unitId || undefined,
        professional_id: professionalId || null,
        appliance_type: applianceType.trim() || null,
        malocclusion_class: malocclusionClass || null,
        diagnosis: diagnosis.trim() || null,
        objectives: objectives.trim() || null,
        started_date: startedDate || null,
        expected_end_date: expectedEndDate || null,
        completed_date: completedDate || null,
        installation_fee: numberOrZero(installationFee),
        monthly_fee: numberOrZero(monthlyFee),
        activations: activations.filter((a) => a.date),
        retention_plan: retentionPlan.trim() || null,
        status,
        notes: notes.trim() || null,
      };

      if (treatment?.id) await updateOrthodonticTreatment(treatment.id, payload);
      else await createOrthodonticTreatment(payload);

      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao salvar tratamento ortodôntico');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0F172A' }}>
          {treatment ? 'Editar tratamento ortodôntico' : 'Novo tratamento ortodôntico'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              size="small"
              options={ORTHODONTIC_APPLIANCES}
              value={applianceType}
              onInputChange={(_, value) => setApplianceType(value)}
              renderInput={(params) => <TextField {...params} label="Aparelho" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="ortho-class-label">Classificação de Angle</InputLabel>
              <Select
                labelId="ortho-class-label"
                label="Classificação de Angle"
                value={malocclusionClass}
                onChange={(e) => setMalocclusionClass(e.target.value)}
              >
                <MenuItem value="">Não definida</MenuItem>
                {MALOCCLUSION_CLASSES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="ortho-status-label">Status</InputLabel>
              <Select labelId="ortho-status-label" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {ORTHODONTIC_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" multiline minRows={2} label="Diagnóstico" value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" multiline minRows={2} label="Objetivos do tratamento" value={objectives}
              onChange={(e) => setObjectives(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Início" InputLabelProps={{ shrink: true }}
              value={startedDate} onChange={(e) => setStartedDate(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Previsão de término" InputLabelProps={{ shrink: true }}
              value={expectedEndDate} onChange={(e) => setExpectedEndDate(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Conclusão" InputLabelProps={{ shrink: true }}
              value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Valor de instalação (R$)" value={installationFee}
              onChange={(e) => setInstallationFee(e.target.value)} placeholder="0,00" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Mensalidade (R$)" value={monthlyFee}
              onChange={(e) => setMonthlyFee(e.target.value)} placeholder="0,00" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="ortho-professional-label">Profissional</InputLabel>
              <Select
                labelId="ortho-professional-label"
                label="Profissional"
                value={professionalId}
                onChange={(e) => setProfessionalId(e.target.value)}
              >
                <MenuItem value="">Não informado</MenuItem>
                {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider textAlign="left" sx={{ my: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Ativações
              </Typography>
            </Divider>
          </Grid>

          {activations.map((activation, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <TextField
                  size="small"
                  type="date"
                  label="Data"
                  InputLabelProps={{ shrink: true }}
                  value={activation.date}
                  onChange={(e) => updateActivation(index, { date: e.target.value })}
                  sx={{ width: { xs: '100%', md: 170 } }}
                />
                <FormControl size="small" sx={{ width: { xs: '100%', md: 150 } }}>
                  <InputLabel id={`ortho-arch-${index}`}>Arcada</InputLabel>
                  <Select
                    labelId={`ortho-arch-${index}`}
                    label="Arcada"
                    value={activation.arch || 'both'}
                    onChange={(e) => updateActivation(index, { arch: e.target.value as OrthodonticActivation['arch'] })}
                  >
                    <MenuItem value="both">Ambas</MenuItem>
                    <MenuItem value="upper">Superior</MenuItem>
                    <MenuItem value="lower">Inferior</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Procedimento"
                  value={activation.notes || ''}
                  onChange={(e) => updateActivation(index, { notes: e.target.value })}
                  placeholder="Ex.: troca de fio 0.16 NiTi"
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}
                />
                <IconButton
                  size="small"
                  onClick={() => setActivations((prev) => prev.filter((_, i) => i !== index))}
                  sx={{ color: '#94A3B8', mt: 0.5 }}
                  aria-label="Remover ativação"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setActivations((prev) => [...prev, { date: todayIso(), arch: 'both', notes: '' }])}
              sx={{ textTransform: 'none', color: '#0D9488' }}
            >
              Registrar ativação
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" multiline minRows={2} label="Plano de contenção" value={retentionPlan}
              onChange={(e) => setRetentionPlan(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" multiline minRows={2} label="Observações" value={notes}
              onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrthodonticTreatmentDialog;
